// packages
import { max, rollups, sum } from 'd3-array';
import { select } from 'd3-selection';
import { stack } from 'd3-shape';
import { intword, intcomma } from 'journalize';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';

// local
import { createBase } from '../../utils/d3-base';
import {
  dateFormat,
  dateParser,
  monthTickValues,
  monthFormat,
} from '../../utils/d3-utils';

export function boot(data, id = 'changes-chart') {
  const container = select(`#${id}`);

  data.forEach((d) => (d.date = dateParser(d.date)));

  // Buttons
  const ele = document.querySelector('.graphic-section--changes');
  const buttons = ele.querySelectorAll('button.toggle');
  const getSelectedVariable = () => {
    const activeButton = ele.querySelector('button.active');
    return activeButton.getAttribute('data-variable');
  };
  buttons.forEach((b) => {
    b.addEventListener('click', function () {
      if (this.classList.contains('active')) {
      } else {
        const activeButton = ele.querySelector('button.active');
        activeButton.classList.remove('active');
        activeButton.classList.add('inactive');
        this.classList.remove('inactive');
        this.classList.add('active');
        const variable = b.getAttribute('data-variable');
        const hed = ele.querySelector('.chart-head');
        if (variable == 'counties') {
          hed.innerHTML = 'Weekly tier assignments by county';
        } else {
          hed.innerHTML = 'Weekly tier assignments by population';
        }
        render();
      }
    });
  });

  render();

  function render() {
    // empty the container on render every time
    container.html('');
    const metric = getSelectedVariable();

    const nestedData = rollups(
      data,
      (d) =>
        metric == 'counties' ? d.length : sum(d.map((t) => t.population)),
      (d) => +d.date,
      (d) => d.tier
    ).map(([key, values]) => {
      const tier1 = values.find(([key]) => key == '1');
      const tier2 = values.find(([key]) => key == '2');
      const tier3 = values.find(([key]) => key == '3');
      const tier4 = values.find(([key]) => key == '4');

      return {
        date: new Date(key),
        1: tier1 ? tier1[1] : [],
        2: tier2 ? tier2[1] : [],
        3: tier3 ? tier3[1] : [],
        4: tier4 ? tier4[1] : [],
      };
    });

    const stackedData = stack()
      .keys(['1', '2', '3', '4'])(nestedData)
      .map((d) => (d.forEach((v) => (v.key = d.key)), d));

    const height = 300;
    const { g, x, y, width, xAxis, yAxis, drawAxis } = createBase({
      container,
      height: height,
      x: scaleBand(),
      y: scaleLinear(),
      margin: { left: metric == 'counties' ? 35 : 60, bottom: 25, top: 10 },
      title: 'Tier reassignements by week',
    });

    // set up our x scale
    const dateRange = nestedData.map((d) => new Date(d.date));
    x.domain(dateRange).padding(0.01);

    // pre-calc the bandwidth
    const bandwidth = x.bandwidth();

    // set up our y scale
    const yMax = max(data, (d) => d.total);
    if (metric == 'counties') {
      y.domain([0, 58]);
    } else {
      y.domain([0, 40_000_000]);
    }

    // tell the x axis we only want these ticks
    xAxis
      .tickValues(dateRange.filter((e, i) => i % 8 === 0))
      .tickFormat(dateFormat);

    // tell the y axis what we want
    if (metric == 'counties') {
      yAxis.tickSize(0).tickValues([10, 20, 30, 40, 50, 58]);
    } else {
      yAxis
        .tickSize(0)
        .tickValues([10_000_000, 20_000_000, 30_000_000, 40_000_000])
        .tickFormat(intword);
    }

    // draw the axis lines, then remove their domains
    drawAxis();

    const color = scaleOrdinal()
      .domain(['1', '2', '3', '4'])
      .range(['#802f67', '#c43d53', '#d97641', '#e6b735']);

    g.append('g')
      .selectAll('g')
      .data(stackedData)
      .join('g')
      .attr('fill', (d) => color(d.key))
      .selectAll('rect')
      .data((d) => d)
      .join('rect')
      .attr('x', (d, i) => x(d.data.date))
      .attr('y', (d) => y(d[1]))
      .attr('height', (d) => y(d[0]) - y(d[1]))
      .attr('width', bandwidth)
      .append('title')
      .text((d) => {
        const s = `${dateFormat(new Date(d.data.date))}: Tier ${d.key}`;
        const value = d[1] - d[0];
        return metric == 'counties'
          ? `${s} (${value} counties)`
          : `${s} (${intcomma(value)} residents)`;
      });
  }

  return render;
}
